import { Link, useRouterState } from "@tanstack/react-router";
import {
  Activity,
  BarChart3,
  ChevronRight,
  CircleDot,
  Database,
  Factory,
  FileText,
  Gauge,
  Layers,
  ListOrdered,
  Radar,
  Receipt,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  Sigma,
  Sparkles,
  UserRound,
  Wallet,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  PORTAL_NAVIGATION_ITEMS,
  WORKSPACE_NAVIGATION_GROUPS,
  portalNavigationItem,
  portalRouteIsActive,
  workspaceNavigationGroup,
} from "@/lib/portal-navigation";
import { usePortalExperience } from "@/lib/portal-experience";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { cn } from "@/lib/utils";
import { PortalMobileNavigation } from "./PortalMobileNavigation";
import { PortalAccountMenu } from "./PortalAccountMenu";
import { PortalExperienceSwitcher } from "./PortalExperienceSwitcher";

const icons = {
  dashboard: Gauge,
  portfolio: Layers,
  signals: Radar,
  records: ListOrdered,
  claims: Receipt,
  safety: ShieldAlert,
  compliance: ScrollText,
  loop: CircleDot,
  top5: Activity,
  prevention: ShieldCheck,
  suppliers: Factory,
  financial: Wallet,
  executive: BarChart3,
  brief: FileText,
  confidence: Database,
};

// Adapted from the September 30 Lovable export. Identity and Division scope
// remain owned by PortalBootstrap and the canonical Broadview API.
export function PortalAppShell({ children }: { children: ReactNode }) {
  const { experience } = usePortalExperience();
  return experience === "workspace" ? (
    <WorkspacePreviewShell>{children}</WorkspacePreviewShell>
  ) : (
    <ClassicPortalShell>{children}</ClassicPortalShell>
  );
}

function ClassicPortalShell({ children }: Readonly<{ children: ReactNode }>) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { tenantContext, mode } = usePortalRuntime();
  return (
    <div className="portal-app-shell flex min-h-screen bg-background text-foreground">
      <a
        href="#portal-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>
      <aside
        aria-label="Broadview workspace"
        className="no-print sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar lg:flex"
      >
        <Link
          to="/"
          aria-label="Broadview dashboard"
          className="flex items-center gap-2.5 border-b border-sidebar-border px-4 py-5"
        >
          <span className="grid size-8 place-items-center rounded bg-primary/15 text-primary">
            <Sigma className="size-4" aria-hidden="true" />
          </span>
          <span>
            <span className="block font-display text-sm font-semibold">Broadview</span>
            <span className="block text-[0.66rem] uppercase tracking-[0.18em] text-muted-foreground">
              Intelligence
            </span>
          </span>
        </Link>
        <nav aria-label="Workspace navigation" className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {PORTAL_NAVIGATION_ITEMS.map((item) => {
            const Icon = icons[item.icon];
            const active = portalRouteIsActive(pathname, item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={active ? "page" : undefined}
                title={item.description}
                className={cn(
                  "flex items-center gap-2.5 rounded px-2.5 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active
                    ? "bg-sidebar-accent text-sidebar-primary"
                    : "text-sidebar-foreground/85 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
                )}
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="space-y-3 border-t border-sidebar-border p-4">
          <PortalExperienceSwitcher compact />
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.16em] text-muted-foreground">
              Selected Division
            </p>
            <p className="mt-1 break-words text-xs font-medium">{tenantContext.tenantName}</p>
            <p className="mt-1 text-[0.68rem] text-muted-foreground">
              {mode === "live" ? "Verified workspace access" : "Fixture workspace"}
            </p>
          </div>
          <Link
            to="/profile"
            className="flex items-center gap-2 rounded border border-sidebar-border px-3 py-2 text-xs hover:bg-sidebar-accent"
          >
            <UserRound className="size-3.5" aria-hidden="true" />
            My profile
          </Link>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <div className="portal-mobile-bar no-print sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-border bg-sidebar px-4 lg:hidden">
          <PortalMobileNavigation liveMode={mode === "live"} />
          <Link
            to="/"
            className="flex min-w-0 items-center gap-2 font-display text-sm font-semibold"
          >
            <Sigma className="size-4 text-primary" aria-hidden="true" />
            Broadview Intelligence
          </Link>
        </div>
        <div id="portal-content" tabIndex={-1} className="portal-page-content outline-none">
          {children}
        </div>
      </div>
    </div>
  );
}

function WorkspacePreviewShell({ children }: Readonly<{ children: ReactNode }>) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { tenantContext, mode } = usePortalRuntime();
  const activeGroup = workspaceNavigationGroup(pathname);

  return (
    <div className="portal-app-shell flex min-h-screen bg-background text-foreground">
      <a
        href="#portal-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>
      <aside
        aria-label="Workspace Preview navigation"
        className="no-print sticky top-0 hidden h-screen w-72 shrink-0 flex-col border-r border-sidebar-border bg-sidebar lg:flex"
      >
        <div className="border-b border-sidebar-border px-4 py-4">
          <Link to="/" aria-label="Broadview dashboard" className="flex items-center gap-2.5">
            <span className="grid size-9 place-items-center rounded-lg bg-primary/15 text-primary">
              <Sigma className="size-4" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-display text-sm font-semibold">Broadview</span>
              <span className="block text-[0.64rem] uppercase tracking-[0.17em] text-muted-foreground">
                Warranty Intelligence
              </span>
            </span>
          </Link>
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-primary/25 bg-primary/10 px-3 py-2 text-xs text-primary">
            <Sparkles className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="font-semibold">Workspace Preview</span>
          </div>
          <div className="mt-3">
            <PortalExperienceSwitcher compact />
          </div>
        </div>

        <nav aria-label="Workspace Preview navigation" className="flex-1 overflow-y-auto p-3">
          <p className="px-2 pb-2 text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Follow the work
          </p>
          <div className="space-y-1.5">
            {WORKSPACE_NAVIGATION_GROUPS.map((group) => {
              const Icon = icons[group.icon];
              const active = activeGroup.id === group.id;
              return (
                <div
                  key={group.id}
                  className={cn(
                    "rounded-xl border transition-colors",
                    active
                      ? "border-primary/30 bg-primary/5"
                      : "border-transparent hover:border-sidebar-border",
                  )}
                >
                  <Link
                    to={group.primaryTo}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-3 rounded-xl px-3 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active
                        ? "text-sidebar-primary"
                        : "text-sidebar-foreground/85 hover:bg-sidebar-accent/60",
                    )}
                  >
                    <span
                      className={cn(
                        "grid size-8 shrink-0 place-items-center rounded-lg",
                        active ? "bg-primary/15" : "bg-sidebar-accent",
                      )}
                    >
                      <Icon className="size-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">{group.label}</span>
                      <span className="mt-0.5 block truncate text-[0.68rem] text-muted-foreground">
                        {group.description}
                      </span>
                    </span>
                    <ChevronRight
                      className={cn("size-3.5 transition-transform", active && "rotate-90")}
                      aria-hidden="true"
                    />
                  </Link>
                  {active && (
                    <div className="mb-2 ml-7 mr-2 border-l border-primary/25 pl-3">
                      {group.routes.map((route) => {
                        const item = portalNavigationItem(route);
                        const routeActive = portalRouteIsActive(pathname, route);
                        return (
                          <Link
                            key={route}
                            to={route}
                            aria-current={routeActive ? "page" : undefined}
                            title={item.description}
                            className={cn(
                              "block rounded-md px-2 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              routeActive
                                ? "bg-sidebar-accent text-sidebar-primary"
                                : "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
                            )}
                          >
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className="space-y-3 border-t border-sidebar-border p-4">
          <div className="rounded-lg border border-sidebar-border bg-background/30 p-3">
            <p className="text-[0.62rem] uppercase tracking-[0.16em] text-muted-foreground">
              Selected Division
            </p>
            <p className="mt-1 break-words text-xs font-medium">{tenantContext.tenantName}</p>
            <p className="mt-1 text-[0.68rem] text-muted-foreground">
              {mode === "live" ? "Same governed data as Classic" : "Same fixture data as Classic"}
            </p>
          </div>
          <Link
            to="/profile"
            className="flex items-center gap-2 rounded border border-sidebar-border px-3 py-2 text-xs hover:bg-sidebar-accent"
          >
            <UserRound className="size-3.5" aria-hidden="true" />
            My profile
          </Link>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <div className="portal-mobile-bar no-print sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-border bg-sidebar px-4 lg:hidden">
          <PortalMobileNavigation liveMode={mode === "live"} />
          <Link
            to="/"
            className="flex min-w-0 flex-1 items-center gap-2 font-display text-sm font-semibold"
          >
            <Sigma className="size-4 shrink-0 text-primary" aria-hidden="true" />
            <span className="truncate">Broadview Intelligence</span>
          </Link>
          <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-1 text-[0.62rem] font-semibold uppercase tracking-wide text-primary">
            Preview
          </span>
        </div>
        <div id="portal-content" tabIndex={-1} className="portal-page-content outline-none">
          {children}
        </div>
      </div>
    </div>
  );
}

export function WorkspacePage({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="no-print sticky top-0 z-30 border-b border-border bg-background/90 px-5 py-4 backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-lg font-semibold">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <PortalAccountMenu />
          </div>
        </div>
      </header>
      <main className="print-full mx-auto w-full max-w-[1500px] space-y-5 p-5">{children}</main>
    </div>
  );
}
